
import { of as observableOf, interval as observableInterval, Observable, Subscription } from 'rxjs';

import { filter, startWith, map, mergeMap } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from 'angular-2-local-storage';

import { SapiService } from '../../../services/sapi.service';

import { NotificationGroup, Notification } from '../../../interfaces/notification';
import { QueryParameter } from '../../../interfaces/queryString';
import { ReleaseService } from '../../../services/release.service';
import { TranslateService } from '@ngx-translate/core';
import { WebSocketsService } from 'app/services/web-sockets.service';

@Component({
	selector: 'bb-notification-center',
	templateUrl: './bb-notification-center.component.html',
	styleUrls: ['./bb-notification-center.component.scss']
})

export class BbNotificationCenterComponent implements OnInit {
	notificationCount = 0;
	minSummarizeCount = 2;
	notificationCenterVisible = false;
	transcodingInterval: number = 20 * 1000;
	transcodingObservable: Observable<Notification>;
	intervalCount = 0;
	subscriptions: Subscription = new Subscription();

	notifications: NotificationGroup[] = [];

	closedNotifications: { [groupName: string]: number } = {};

	constructor(
		private sapiService: SapiService,
		private localStorageService: LocalStorageService,
		private router: Router,
		private releaseService: ReleaseService,
		private translate: TranslateService,
		private webSocketsService: WebSocketsService
	) { }

	ngOnInit() {
		const localStorageClosedNotifications = this.localStorageService.get('ovp-closedNotifications');

		if (localStorageClosedNotifications != null) {
			this.closedNotifications = localStorageClosedNotifications as any;
		} else {
			// Set default from current time.
			const now = new Date();
			this.closedNotifications.transcoding = now.getTime();
			this.localStorageService.set('ovp-closedNotifications', this.closedNotifications);
		}

		// TODO: here we still need to do piping

		this.transcodingObservable = observableInterval(this.transcodingInterval).pipe(
			startWith(0),
			mergeMap(() =>
				this.getTranscodingData().pipe(
					mergeMap((data: any) => data.items),
					filter((data: any) => this.transcodingFilter(data)),
					map((data: any) => this.transcodingAdapter(data)))
			),
			filter((notification: Notification) => this.notificationFilter(notification)));

		this.transcodingObservable.subscribe((notification: Notification) => this.processNotification(notification));

		this.releaseService.releaseNotificationSubject.subscribe((notification: Notification) => this.processNotification(notification));

		this.webSocketsService.bbNotificationChannels.subscribe((channelName: string) => {
			if (channelName) {
				let notification;
				let fileName;
				let reportUrl;
				this.subscriptions.add(this.webSocketsService.subscribeToChannel(channelName).subscribe((websocketEvent: any) => {
					if (websocketEvent && websocketEvent.ev && websocketEvent.payload) {
						if (websocketEvent.ev === 'finish') {
							if (websocketEvent.status === 'done') {
								reportUrl = websocketEvent.payload.Url;
								fileName = websocketEvent.payload.Filename;
								notification = {
									group: this.translate.instant('REPORT_DOWNLOAD'),
									label: fileName,
									date: new Date(),
									entityId: 0,
									downloadUrl: reportUrl + fileName
								};
								this.webSocketsService.unsubscribeFromChannel(channelName);
							} else if (websocketEvent.status === 'error') {
								notification = {
									group: this.translate.instant('ERROR'),
									label: this.translate.instant('REPORT_ERROR_GENERATING'),
									date: new Date(),
									entityId: 0,
									entityUrl: ''
								};
								this.webSocketsService.unsubscribeFromChannel(channelName);
							}
							this.processNotification(notification);
						}
					}
				}));
			}
		});
	}

	processNotification(notification: Notification): void {
		if (!this.groupExists(notification.group)) {
			let newGroup;
			switch (notification.group) {
				case 'transcoding':
					newGroup = {
						name: notification.group,
						label: 'Transcoding finished',
						summarizeLabel: 'media clips finshed transcoding',
						summarize: false,
						icon: 'fa-server',
						latestDate: new Date(0),
						items: []
					};
					break;

				case this.translate.instant('VERSION_UPDATE'):
					newGroup = {
						name: notification.group,
						label: this.translate.instant('VERSION_UPDATE_LABEL'),
						summarizeLabel: this.translate.instant('VERSION_UPDATE_SUMMARY'),
						summarize: false,
						icon: 'fa-info-circle',
						latestDate: new Date(0),
						items: []
					};
					break;

				case this.translate.instant('REPORT_DOWNLOAD'):
					newGroup = {
						name: notification.group,
						label: this.translate.instant('REPORT_DOWNLOAD_LABEL'),
						summarize: false,
						icon: 'fa-info-circle',
						latestDate: new Date(0),
						items: []
					};
					break;

				case this.translate.instant('ERROR'):
					newGroup = {
						name: notification.group,
						label: this.translate.instant('REPORT_ERROR_GENERATING'),
						summarize: false,
						icon: 'fa-info-circle',
						latestDate: new Date(0),
						items: []
					};
					break;

				default:
					// If we can't find a suitable group, create a generic one
					newGroup = {
						name: notification.group,
						label: notification.label,
						icon: 'fa-info-circle',
						latestDate: new Date(0),
						items: []
					};
					break;
			}
			this.notifications.push(newGroup);
		}
		for (const key in this.notifications) {
			if (this.notifications[key].name === notification.group) {
				this.notifications[key].items.push(notification);
				if (this.notifications[key].latestDate.getTime() < notification.date.getTime()) {
					this.notifications[key].latestDate = notification.date;
				}
				this.summarizeGroups();
			}
		}

		this.updateNotificationCount();
	}

	groupExists(name: string): boolean {
		for (const group of this.notifications) {
			if (group.name === name) {
				return true;
			}
		}
		return false;
	}

	summarizeGroups(): void {
		for (const group of this.notifications) {
			if (group.items.length > this.minSummarizeCount) {
				group.summarize = true;
			}
		}
	}

	goToEntity(notification: Notification): void {
		if (notification.group === 'version update') {
			window.location.reload();
		} else {
			this.router.navigate([notification.entityUrl]);
			this.showNotificationCenter(false);
		}
	}

	closeGroup(group: NotificationGroup, ev: MouseEvent): boolean {
		this.closedNotifications[group.name] = new Date().getTime();

		for (let i = 0, len = this.notifications.length; i < len; i++) {
			if (this.notifications[i].name === group.name) {
				this.notifications.splice(i, 1);

				this.updateNotificationCount();
				localStorage.setItem('ovp-closedNotifications', JSON.stringify(this.closedNotifications));
				ev.stopPropagation();
				return false;
			}
		}
	}

	showNotificationCenter(state: boolean): void {
		// Always show...
		// if (state && this.notificationCount > 0) {
		// 	this.notificationCenterVisible = true;
		// } else {
		// 	this.notificationCenterVisible = false;
		// }
		this.notificationCenterVisible = state;
		this.summarizeGroups();
	}

	updateNotificationCount(): void {
		let total = 0;

		for (const group of this.notifications) {
			total += group.items.length;
		}

		this.notificationCount = total;
	}

	getTranscodingData(): Observable<any> {
		if (!this.localStorageService.get('ovp-sessionToken')) {
			// not logged in
			return observableOf({ items: [] });
		}

		// Get from last closed date,  ... max 12 hours
		const lastDate = new Date(this.closedNotifications.transcoding);

		const maxDate = new Date();
		maxDate.setHours(maxDate.getHours() - 12);

		const fromDate = (maxDate.getTime() < lastDate.getTime() ? maxDate : lastDate);

		const queryParameters: QueryParameter[] = [];
		queryParameters.push({ name: 'finisheddate', values: [fromDate.toISOString()] } as QueryParameter);
		return this.sapiService.query('transcoding', queryParameters);
	}

	notificationFilter(notification: Notification): boolean {
		if (!(notification.group in this.closedNotifications)) {
			return true;
		}

		const groupDate = new Date(this.closedNotifications[notification.group]);

		if (groupDate.getTime() < notification.date.getTime()) {
			return true;
		}

		return false;
		// return !((notification.group in this.closedNotifications) && this.closedNotifications[notification.group] < notification.date);
	}

	transcodingAdapter(data: any): Notification {
		const date = this.getTranscodingFinishedDate(data);
		const notification: Notification = {
			group: 'transcoding',
			label: data.title || data.originalfilename || data.id,
			entityId: data.id,
			entityUrl: '/library/mediaclip/' + data.id + '/metadata', // Hmm, rather navigate to state, but thats angular 1..
			entity: data,
			date
		};
		return notification;
	}

	transcodingFilter(data: any): boolean {
		for (const job of data.mediajobs) {
			if (job.status === 'bbqueued' || job.status === 'running') {
				// Don't return jobs that are currently running
				return false;
			}
		}

		if (this.getTranscodingFinishedDate(data).getTime() === new Date(0).getTime()) {
			// Don't return jobs that haven't finished
			return false;
		}

		// Filter double transcodings
		if (!this.groupExists('transcoding')) {
			return true;
		}

		for (const group of this.notifications) {
			if (group.name === 'transcoding') {
				for (const item of group.items) {
					if (item.entityId === data.id) {
						return false;
					}
				}
			}
		}

		return true;
	}

	getTranscodingFinishedDate(data: any): Date {
		let date = new Date(0);

		for (const job of data.mediajobs) {
			const jobdate = new Date(job.finisheddate);
			if ('finisheddate' in job && jobdate.getTime() > date.getTime()) {
				date = new Date(job.finisheddate);
			}
		}
		return date;
	}
}
